import { configureStore } from "@/lib/store"

import { billingService } from "../services/billing-service"
import {
  ApiPlan,
  ApiSubscription,
  Plan,
  PlanType,
  Subscription,
  SubscriptionStatus,
} from "../types"

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(amount / 100)
}

const mapApiPlansToPlans = (apiPlans: ApiPlan[]): Plan[] => {
  return apiPlans.map(apiPlan => ({
    id: apiPlan.id,
    name: apiPlan.name,
    price: apiPlan.price,
    formattedPrice: formatCurrency(apiPlan.price),
    type: apiPlan.type as PlanType,
    features: Object.keys(apiPlan.features),
    interval: apiPlan.billing_interval,
  }))
}

const mapApiSubscriptionToSubscription = (apiSubscription: ApiSubscription): Subscription => {
  return {
    id: apiSubscription.id,
    planId: apiSubscription.plan_id,
    status: apiSubscription.status as SubscriptionStatus,
    billingInterval: apiSubscription.billing_interval,
    startDate: apiSubscription.start_date,
    endDate: apiSubscription.end_date,
    trialEndDate: apiSubscription.trial_end,
    autoRenew: apiSubscription.auto_renew,
    currentPeriodStart: apiSubscription.current_period_start,
    currentPeriodEnd: apiSubscription.current_period_end,
    remainingCredits: apiSubscription.remaining_credits,
    includedCredits: apiSubscription.included_credits,
    paymentMethodType: apiSubscription.payment_method_type,
    paymentMethodCreditCardLastFourDigits:
      apiSubscription.payment_method_credit_card_last_four_digits,
    paymentMethodCreditCardBrand: apiSubscription.payment_method_credit_card_brand,
    provider: apiSubscription.provider,
  }
}

export interface SubscriptionState {
  currentSubscription: Subscription | null
  plans: Plan[] | null
  isLoading: boolean
  error: string | null
  actions: {
    fetchBillingInfo: () => Promise<void>
    // Future action: updateSubscriptionPlan: (planId: string) => Promise<void>;
  }
}

export const useSubscriptionStore = configureStore<SubscriptionState>(
  (set, get) => ({
    currentSubscription: null,
    plans: null,
    isLoading: false,
    error: null,
    actions: {
      fetchBillingInfo: async () => {
        if (get().isLoading || get().error) return
        set({ isLoading: true, error: null })
        try {
          const [subscriptionData, plansData] = await Promise.allSettled([
            billingService.getCurrentSubscription(),
            billingService.getAvailablePlans(),
          ])
          if (subscriptionData.status === "rejected") {
            console.log("subscriptionData", subscriptionData.reason)
            set({ error: subscriptionData.reason, isLoading: false })
            return
          }
          if (plansData.status === "rejected") {
            console.log("plansData", plansData.reason)
            set({ error: plansData.reason, isLoading: false })
            return
          }
          set({
            currentSubscription:
              subscriptionData.status === "fulfilled"
                ? mapApiSubscriptionToSubscription(subscriptionData.value)
                : null,
            plans: plansData.status === "fulfilled" ? mapApiPlansToPlans(plansData.value) : [],
            isLoading: false,
          })
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "An unknown error occurred"
          set({ error: errorMessage, isLoading: false })
          console.error("Failed to fetch billing info:", err)
        }
      },
      // Future action implementation
      // updateSubscriptionPlan: async (planId: string) => {
      //   set({ isLoading: true, error: null });
      //   try {
      //     const updatedSubscription = await billingService.updateSubscription(planId);
      //     set({ currentSubscription: updatedSubscription, isLoading: false });
      //     // Optionally, re-fetch plans if they might change or have dynamic properties
      //     // await get().actions.fetchBillingInfo(); // or just the plans part
      //   } catch (err) {
      //     const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
      //     set({ error: errorMessage, isLoading: false });
      //     console.error("Failed to update subscription:", err);
      //   }
      // },
    },
  })
  /*{
    devtools: {
      name: "subscription-store",
    },
    // Example for persistent state if needed, e.g., for currentSubscription only
    // partialize: (state) => ({ currentSubscription: state.currentSubscription }),
  }*/
)
